import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import styled, { ThemeProvider } from 'styled-components';
import { Head, Nav, Social, Email, Footer } from '@components';
import WhatsAppFab from './whatsapp-fab';
import { GlobalStyle, theme } from '@styles';
import { LanguageProvider, useLanguage } from '../context/language';

const StyledContent = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

const normalizePathname = pathname => {
  if (!pathname) {
    return '/';
  }

  if (pathname === '/') {
    return '/';
  }

  return pathname.replace(/\/+$/, '');
};

const isHomePath = pathname => {
  const normalized = normalizePathname(pathname);
  return normalized === '/' || normalized === '/en' || normalized === '/fr';
};

const LayoutContent = ({ children, location, headProps }) => {
  const { content } = useLanguage();
  const isHome = isHomePath(location.pathname);

  // Sets target="_blank" rel="noopener noreferrer" on external links
  const handleExternalLinks = () => {
    const allLinks = Array.from(document.querySelectorAll('a'));
    if (allLinks.length > 0) {
      allLinks.forEach(link => {
        if (link.host !== window.location.host) {
          link.setAttribute('rel', 'noopener noreferrer');
          link.setAttribute('target', '_blank');
        }
      });
    }
  };

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.substring(1); // location.hash without the '#'
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView();
          el.focus();
        }
      }, 0);
    }

    handleExternalLinks();
  }, [location.hash]);

  return (
    <>
      <Head {...headProps} />

      <div id="root">
        <ThemeProvider theme={theme}>
          <GlobalStyle />

          <a className="skip-to-content" href="#content">
            {content.layout.skipToContent}
          </a>

          <StyledContent>
            <Nav isHome={isHome} />
            <Social isHome={isHome} />
            <Email isHome={isHome} />
            <WhatsAppFab />

            <div id="content">
              {children}
              <Footer />
            </div>
          </StyledContent>
        </ThemeProvider>
      </div>
    </>
  );
};

LayoutContent.propTypes = {
  children: PropTypes.node.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    hash: PropTypes.string,
  }).isRequired,
  headProps: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    image: PropTypes.string,
    noindex: PropTypes.bool,
    pathname: PropTypes.string,
    schemaNodes: PropTypes.arrayOf(PropTypes.object),
  }),
};

LayoutContent.defaultProps = {
  headProps: null,
};

const Layout = ({ children, location, language, headProps }) => (
  <LanguageProvider initialLanguage={language}>
    <LayoutContent location={location} headProps={headProps}>
      {children}
    </LayoutContent>
  </LanguageProvider>
);

Layout.propTypes = {
  children: PropTypes.node.isRequired,
  location: PropTypes.object.isRequired,
  language: PropTypes.string,
  headProps: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    image: PropTypes.string,
    noindex: PropTypes.bool,
    pathname: PropTypes.string,
    schemaNodes: PropTypes.arrayOf(PropTypes.object),
  }),
};

Layout.defaultProps = {
  language: null,
  headProps: null,
};

export default Layout;
